import React, { useState, Fragment } from "react";
import Select from "components/Form/fields/Select";
import { Formik, useFormikContext } from "formik";
import SegmentedControl from "components/SegmentedControl";
import { getAppPageRouteOptions } from "utils/application";
import { APP_PUBLISH_INTENT } from "utils/constants/application";
import Button from "components/Form/fields/Button";
import {
  useActiveManifestsForApplicationRoute,
  useCtxReferences,
  useIsAuthed
} from "utils/selectors";
import { isServer } from "utils/view";
import { UI_THEME } from "utils/constants/ui";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { getManifestLiveUrl } from "utils/manifest";
import { getPublishPhases, validatePublishLocationSchema } from "utils/publish";
import { ORGANIZATION } from "utils/constants/models";
import { useAppUrlVariations } from "utils/hooks/application";
import PhaseList from "components/PhaseList";
import DebounceInput from "components/DebounceInput";
import classnames from "classnames";
import { ensureLeadingSlash } from "utils/route";
import { FORM_KEY } from "utils/constants/form";
import { useIsMounted } from "utils/hooks";
import { STATE_KEYS } from "utils/constants/state";
import UnauthedAccountPrompt from "components/UnauthedAccountPrompt";
import RouteSuggestions from "./RouteSuggestions";
import { usePublishLocationInitialValues } from "utils/hooks/publish";

const PublishLocationForm = ({ setShow, publish }) => {
  const {
    values,
    errors,
    setFieldValue,
    setFieldTouched,
    touched
  } = useFormikContext();
  const isMounted = useIsMounted();

  const [isPublishing, setIsPublishing] = useState(false);
  const { application, resource } = useCtxReferences();
  const isOrg = resource === ORGANIZATION;

  const { existingRoutes } = values[FORM_KEY];
  const hasExistingRoutes = !isEmpty(existingRoutes);

  /**
   * Existing route
   */
  const existingRoute = get(values, STATE_KEYS.PUBLISH.EXISTING_ROUTE);
  const existingRouteTouched = get(touched, STATE_KEYS.PUBLISH.EXISTING_ROUTE);
  const existingRouteError = get(errors, STATE_KEYS.PUBLISH.EXISTING_ROUTE);

  /**
   * New Route
   */
  const newRoute = get(values, STATE_KEYS.PUBLISH.NEW_ROUTE);
  const newRouteTouched = get(touched, STATE_KEYS.PUBLISH.NEW_ROUTE);
  const newRouteError = get(errors, STATE_KEYS.PUBLISH.NEW_ROUTE);

  const publishIntent = get(values, STATE_KEYS.PUBLISH.INTENT);
  const existingIntent = publishIntent === APP_PUBLISH_INTENT.EXISTING;

  /**
   * CTX values
   */
  const ctxRoute =
    publishIntent === APP_PUBLISH_INTENT.EXISTING ? existingRoute : newRoute;
  const targetRoute = ensureLeadingSlash(ctxRoute);
  const hasTargetRoute = ctxRoute && ctxRoute !== "/";

  const ctxRouteTouched =
    publishIntent === APP_PUBLISH_INTENT.EXISTING
      ? existingRouteTouched
      : newRouteTouched;

  const ctxRouteError =
    publishIntent === APP_PUBLISH_INTENT.EXISTING
      ? existingRouteError
      : newRouteError;

  const manifestLiveVariation = values[FORM_KEY].manifestLiveVariation;
  const deployedAtTarget = Boolean(
    manifestLiveVariation && manifestLiveVariation.route === targetRoute
  );

  const activeRouteManifests = useActiveManifestsForApplicationRoute(
    targetRoute
  );
  const publishingDisabled =
    deployedAtTarget || !hasTargetRoute || !isEmpty(ctxRouteError);

  /**
   * If there are any manifests active at the current target route
   * Then a publish action to that route would be a replacement action
   */
  const replacementDeploy = activeRouteManifests.length > 0;

  const urlVariations = useAppUrlVariations();

  const publishPhases = getPublishPhases({
    urlRoot: urlVariations.root,
    replacementDeploy,
    manifestLiveVariation,
    targetRoute,
    touched: ctxRouteTouched,
    error: Boolean(ctxRouteTouched && ctxRouteError) ? ctxRouteError : null,
    errorType: errors && errors.type
  });

  const actions = [
    {
      copy: "Publish",
      theme: UI_THEME.SLIM,
      disabled: publishingDisabled,
      loading: isPublishing,
      onClick: () => {
        setIsPublishing(true);
        const publishParams = {
          route: targetRoute
        };

        publish(publishParams, (err, response) => {
          setIsPublishing(false);

          if (!err) {
            setShow(false);

            if (response && response.manifest) {
              const publishedLiveUrl = getManifestLiveUrl({
                manifest: response.manifest,
                application: response.application,
                resource
              });
              if (publishedLiveUrl && !isServer()) {
                window.open(publishedLiveUrl, "_blank");
              }
            }
          }
        });
      }
    }
  ];

  const pageTargets = getAppPageRouteOptions(application);
  const routeOptions = pageTargets.map((page) => {
    return {
      uid: page.route,
      label: page.route
    };
  });
  routeOptions.unshift({
    uid: "",
    label: "Select a location"
  });

  const selectProps = {
    value: existingRoute,
    options: routeOptions,
    labelTransform: (val) => val,
    onChange: () => (evt) => {
      const selection = evt.currentTarget.value;
      setFieldValue(STATE_KEYS.PUBLISH.EXISTING_ROUTE, selection);

      if (!existingRouteTouched) {
        setFieldTouched(STATE_KEYS.PUBLISH.EXISTING_ROUTE, true);
      }
    }
  };

  const actionButtons =
    actions &&
    actions.map((action, actionIx) => {
      return <Button key={actionIx} {...action} />;
    });

  let settingsSection;
  const liveUrl = values[FORM_KEY].liveUrl;

  return (
    <Fragment>
      <div className="f5 pb2 fw6">Target</div>
      <div className="f6 lh-copy">Set the target location for your page.</div>
      {hasExistingRoutes && (
        <div className="pt3">
          <SegmentedControl
            value={publishIntent}
            theme="slim"
            options={[
              {
                uid: APP_PUBLISH_INTENT.NEW,
                label: "New"
              },
              {
                uid: APP_PUBLISH_INTENT.EXISTING,
                label: "Existing"
              }
            ]}
            onClick={(_, selection) => {
              setFieldValue("intent", selection);
            }}
          />
        </div>
      )}
      <div className="pt3">
        {existingIntent ? (
          <Select {...selectProps} />
        ) : (
          <DebounceInput
            hideError
            name={STATE_KEYS.PUBLISH.NEW_ROUTE}
            inputValue={newRoute}
            placeholder="Enter a location"
            customClasses={{
              input: classnames("input--common br-6")
            }}
            debounce={1000}
            valueSet={(val) => {
              if (isMounted.current) {
                setFieldValue(STATE_KEYS.PUBLISH.NEW_ROUTE, val);

                if (!newRouteTouched) {
                  setTimeout(() => {
                    setFieldTouched(STATE_KEYS.PUBLISH.NEW_ROUTE, true);
                  }, 0);
                }
              }
            }}
          />
        )}
      </div>
      {publishIntent === APP_PUBLISH_INTENT.NEW && <RouteSuggestions />}
      <div className="pt3 mt2">
        <div className="field__label fw5 db pb2">Summary</div>
        <PhaseList phases={publishPhases} clamp />
      </div>
      <div
        className={classnames(
          "w-100 flex flex-row items-center justify-end pv3",
          {
            "bb hairline-1": liveUrl || !isOrg
          }
        )}
      >
        <div className="flex flex-row justify-end">{actionButtons}</div>
      </div>
      {settingsSection}
    </Fragment>
  );
};

const AuthedPublishLocation = ({ setShow, publish }) => {
  const initialValues = usePublishLocationInitialValues();
  return (
    <Formik
      initialValues={initialValues}
      validate={validatePublishLocationSchema}
    >
      {() => <PublishLocationForm setShow={setShow} publish={publish} />}
    </Formik>
  );
};

const UnauthedPublishLocation = () => (
  <UnauthedAccountPrompt
    header="Publish your own pages"
    subheader="Sign up to create and publish pages using your own products and prices."
  />
);

const PublishLocation = (props) => {
  const isAuthed = useIsAuthed();
  return isAuthed ? (
    <AuthedPublishLocation {...props} />
  ) : (
    <UnauthedPublishLocation />
  );
};

export default PublishLocation;
